import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";
import { useAuth } from "./AuthContext";
import {
  getSlackInstallUrl,
  getSlackStatus,
  sendSlackTestNotification,
} from "./api";
import { canManageIntegrations, VIEWER_READ_ONLY_MESSAGE } from "./orgPermissions";
import "./SlackConnectionPanel.css";

function getErrorMessage(err) {
  return err?.message || err?.error || "Something went wrong. Please try again.";
}

export default function SlackConnectionPanel() {
  const { user } = useAuth();
  const integrationsAllowed = canManageIntegrations(user);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [testSuccess, setTestSuccess] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getSlackStatus();
      setStatus(data);
    } catch (err) {
      setError(getErrorMessage(err));
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("slack") === "connected") {
      loadStatus();
    }
  }, [loadStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    setError("");
    setTestSuccess(false);
    try {
      const data = await getSlackInstallUrl({
        redirectAfterInstall: "/?slack=connected",
      });
      if (data?.installUrl) {
        window.location.href = data.installUrl;
        return;
      }
      setError("Slack install URL was not returned.");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setConnecting(false);
    }
  };

  const handleTestNotification = async () => {
    setTesting(true);
    setError("");
    setTestSuccess(false);
    try {
      const result = await sendSlackTestNotification();
      if (result?.delivered) {
        setTestSuccess(true);
        return;
      }
      if (result?.skippedReason) {
        setError(`Test notification skipped (${result.skippedReason}).`);
        return;
      }
      setError("Test notification could not be delivered.");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setTesting(false);
    }
  };

  const isConnected = Boolean(status?.active);
  const reconnectNeeded = Boolean(status?.reconnectRecommended);
  const isDisconnected = !status?.installed;

  return (
    <section className="slack-connection-panel" aria-labelledby="slack-connection-heading">
      <div className="slack-connection-header">
        <h3 id="slack-connection-heading">Slack Notifications</h3>
        <button
          type="button"
          className="secondary-button slack-connection-refresh"
          onClick={loadStatus}
          disabled={loading || connecting || testing}
          aria-label="Refresh Slack status"
        >
          <RefreshCw size={16} />
          Refresh status
        </button>
      </div>

      {loading ? (
        <p className="slack-connection-muted">Loading Slack status…</p>
      ) : isConnected ? (
        <div className="slack-connection-connected">
          <div className="slack-connection-status-line">
            <CheckCircle2 size={18} aria-hidden="true" />
            <strong>Slack connected</strong>
          </div>
          {status?.teamName ? (
            <p>
              Workspace: <span className="slack-connection-value">{status.teamName}</span>
            </p>
          ) : null}
          {status?.channel ? (
            <p>
              Channel: <span className="slack-connection-value">{status.channel}</span>
            </p>
          ) : null}
        </div>
      ) : reconnectNeeded ? (
        <div className="slack-connection-reconnect">
          <div className="slack-connection-status-line">
            <AlertCircle size={18} aria-hidden="true" />
            <strong>Slack needs to be reconnected.</strong>
          </div>
          <p className="slack-connection-muted">
            The app may have been removed from Slack or its access was revoked.
          </p>
          {status?.deactivationReason ? (
            <p>
              Reason:{" "}
              <span className="slack-connection-value">{status.deactivationReason}</span>
            </p>
          ) : null}
        </div>
      ) : (
        <p className="slack-connection-muted">
          Slack is not connected yet. Connect Slack to send RenderRepo notifications to
          your team channel.
        </p>
      )}

      {error ? (
        <div className="error-message slack-connection-error" role="alert">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      ) : null}

      {testSuccess ? (
        <div className="slack-connection-success" role="status">
          <CheckCircle2 size={16} aria-hidden="true" />
          <span>Test notification sent.</span>
        </div>
      ) : null}

      <div className="slack-connection-actions">
        {!integrationsAllowed ? (
          <p className="slack-connection-muted">{VIEWER_READ_ONLY_MESSAGE}</p>
        ) : isDisconnected ? (
          <button
            type="button"
            className="primary-button"
            onClick={handleConnect}
            disabled={connecting || loading}
          >
            {connecting ? "Opening Slack…" : "Connect Slack"}
          </button>
        ) : reconnectNeeded ? (
          <button
            type="button"
            className="primary-button"
            onClick={handleConnect}
            disabled={connecting || loading}
          >
            {connecting ? "Opening Slack…" : "Reconnect Slack"}
          </button>
        ) : (
          <button
            type="button"
            className="primary-button"
            onClick={handleTestNotification}
            disabled={testing || loading}
          >
            {testing ? "Sending…" : "Send test notification"}
          </button>
        )}
      </div>
    </section>
  );
}
